import { Box, Button, Flex, TextInput } from "@mantine/core";
import { IOpenedAppModal } from "@mctypes/frontend.types";
import { IIssueApi } from "@schemas/issueSchema";
import {
  Background,
  BackgroundVariant,
  Connection,
  ConnectionLineType,
  ReactFlow,
  Edge as RFEdge,
  EdgeChange as RFEdgeChange,
  Node as RFNode,
  NodeChange as RFNodeChange,
  OnEdgesChange as RFOnEdgesChange,
  OnNodesChange as RFOnNodesChange,
  useEdgesState,
  useNodesState,
  useReactFlow,
  XYPosition,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { CellStyle, ColDef, GetRowIdParams, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePutIssueConnectMutation } from "../../../../../../openapi";
import { addAppModalThunk, setShowFailedMessageThunk } from "../../../../../global/globalSlice";
import { selectCurrentProjectId } from "../../../../../global/globalSliceCommon";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { AppModalEnums } from "../../../../../mc-shared/sharedConstants";
import { useUploadFileToAzureBlobStorageMutation } from "../../../../azureBlobStorageRTK";
import {
  usePostProjectByProjectIdIssueboardNoteboardImageNodeAddMutation,
  usePostProjectByProjectIdIssueboardNoteboardImageNodeDeleteMutation,
  usePostProjectByProjectIdIssueboardNoteboardImageNodeUpdateMutation,
} from "../../../../../../openapi";
import { useDoUpdateIssueMutation } from "../../issueRTKLegacy";
import { selectIssueBoardId } from "../../redux/selectors/issueCommonSelectors";
import KanbanIssueNode from "./KanbanIssueNode";
import "./lappeplan.css";
import LappeplanImage from "./LappeplanImage";
import { selectFlowEdges, selectFlowNodes, selectIssuesWithoutNoteboardNode } from "./noteboardSelectors";

// ---------- Nodtyper (discriminerad union) ----------
type IssueNodeData = { issue: IIssueApi };
type ImageNodeData = { path: string };

type IssueFlowNode = RFNode<IssueNodeData> & { type: "issueNode" };
type ImageFlowNode = RFNode<ImageNodeData> & { type: "imageNode" };
type FlowNode = IssueFlowNode | ImageFlowNode;

type FlowEdge = RFEdge;
type FlowNodeChange = RFNodeChange<FlowNode>;
type FlowOnNodesChange = RFOnNodesChange<FlowNode>;
type FlowEdgeChange = RFEdgeChange;
type FlowOnEdgesChange = RFOnEdgesChange;

// type guards
const isImageNode = (n: FlowNode | undefined): n is ImageFlowNode => !!n && n.type === "imageNode";
const isIssueNode = (n: FlowNode | undefined): n is IssueFlowNode => !!n && n.type === "issueNode";

export const Lappeplan: React.FC = () => {
  const { screenToFlowPosition } = useReactFlow();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const projectId = useAppSelector(selectCurrentProjectId);
  const issueBoardId = useAppSelector(selectIssueBoardId);

  // 1) Data från Redux
  const flowNodes = useAppSelector(selectFlowNodes) as FlowNode[];
  const flowEdges = useAppSelector(selectFlowEdges) as FlowEdge[];

  // 2) Ephemeral RF-state
  const [nodes, setNodes, onNodesChangeInner] = useNodesState<FlowNode>(flowNodes);
  const [edges, setEdges, onEdgesChangeInner] = useEdgesState<FlowEdge>(flowEdges);

  // Hjälpare
  const getNodeById = useCallback((id: string) => nodes.find((n) => n.id === id), [nodes]);

  // 3) Håll RF i synk med Redux
  useEffect(() => setNodes(flowNodes), [flowNodes, setNodes]);
  useEffect(() => setEdges(flowEdges), [flowEdges, setEdges]);

  // Center-beräkning
  const rfRef = useRef<HTMLDivElement | null>(null);
  const getCenter = useCallback((): XYPosition => {
    const el = rfRef.current;
    if (!el) return { x: 0, y: 0 };

    const rect = el.getBoundingClientRect();
    // skärmkoordinaterna för mitten av React Flow-containern
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;

    return screenToFlowPosition({ x: cx, y: cy });
  }, [screenToFlowPosition]);

  // 4) Mutations
  const dispatch = useAppDispatch();
  const [updateIssue] = useDoUpdateIssueMutation();
  const [connectIssues] = usePutIssueConnectMutation();
  const [updateImageNode] = usePostProjectByProjectIdIssueboardNoteboardImageNodeUpdateMutation();
  const [deleteImageNode] = usePostProjectByProjectIdIssueboardNoteboardImageNodeDeleteMutation();
  const [uploadFile] = useUploadFileToAzureBlobStorageMutation();
  const [addImageNode] = usePostProjectByProjectIdIssueboardNoteboardImageNodeAddMutation();

  // 5) Node changes
  const onNodesChange: FlowOnNodesChange = useCallback(
    (changes: FlowNodeChange[]) => {
      onNodesChangeInner(changes);

      // persistera asynkront
      (async () => {
        for (const c of changes) {
          try {
            if (c.type === "position" && c.dragging === false) {
              const n = getNodeById(c.id);
              if (!n) continue;

              if (isImageNode(n)) {
                await updateImageNode({
                  projectId,
                  body: {
                    issueBoardId,
                    noteboardImageNode: {
                      _id: n.id,
                      path: n.data.path,
                      x: n.position.x,
                      y: n.position.y,
                      width: n.width ?? 900,
                      height: n.height ?? 600,
                    },
                  },
                }).unwrap();
              } else if (isIssueNode(n)) {
                await updateIssue({
                  projectId,
                  issueBoardId,
                  issueId: n.id,
                  attr: "noteboardNode",
                  value: { visible: true, x: n.position.x, y: n.position.y },
                  oldValue: null,
                }).unwrap();
              }
            }

            if (c.type === "dimensions" && c.resizing === false) {
              const n = getNodeById(c.id);
              if (isImageNode(n)) {
                await updateImageNode({
                  projectId,
                  body: {
                    issueBoardId,
                    noteboardImageNode: {
                      _id: n.id,
                      path: n.data.path,
                      x: n.position.x,
                      y: n.position.y,
                      width: n.width ?? 900,
                      height: n.height ?? 600,
                    },
                  },
                }).unwrap();
              }
            }

            if (c.type === "remove") {
              const n = getNodeById(c.id);
              if (!n) continue;

              if (isImageNode(n)) {
                await deleteImageNode({
                  projectId,
                  body: {
                    issueBoardId,
                    noteboardImageNodeId: n.id,
                  },
                }).unwrap();
              } else if (isIssueNode(n)) {
                await updateIssue({
                  projectId,
                  issueBoardId,
                  issueId: n.id,
                  attr: "noteboardNode",
                  value: { ...(n.data.issue.noteboardNode ?? {}), visible: false },
                  oldValue: null,
                }).unwrap();
              }
            }
          } catch {
            //felhantering sker i rtk-slice
          }
        }
      })();
    },
    [
      onNodesChangeInner,
      getNodeById,
      updateImageNode,
      deleteImageNode,
      updateIssue,
      projectId,
      issueBoardId,
      dispatch,
      setNodes,
      flowNodes,
    ],
  );

  const onEdgesChange: FlowOnEdgesChange = useCallback(
    (changes: FlowEdgeChange[]) => {
      onEdgesChangeInner(changes);
      changes.forEach(async (chg) => {
        if (chg.type !== "remove") return;
        const edge = edges.find((e) => e.id === chg.id);
        if (!edge) return;

        // Låt endpointens onQueryStarted hantera fel + toast.
        void connectIssues({
          body: {
            projectId,
            issueBoardId,
            sourceIssueId: edge.source,
            targetIssueId: edge.target,
            action: "DISCONNECT",
          },
        });
      });
    },
    [onEdgesChangeInner, edges, connectIssues, projectId, issueBoardId],
  );

  const handlePickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 1) läs naturlig storlek
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;
    await new Promise((r) => (img.onload = r));
    const { width: naturalW, height: naturalH } = img;
    URL.revokeObjectURL(objectUrl);

    // 2) ladda upp fil
    let url = "";
    try {
      const up = await uploadFile({
        projectId,
        file,
        path: `${projectId}/issueboard/${issueBoardId}/imageNodes`,
      }).unwrap();
      url = up.url;
    } catch {
      dispatch(setShowFailedMessageThunk("Kunne ikke laste opp bilde"));
      return;
    }

    // 3) spara i backend – ingen lokal patch
    try {
      const pos = getCenter();
      await addImageNode({
        projectId,
        body: {
          issueBoardId,
          noteboardImageNode: { path: url, x: pos.x, y: pos.y, width: naturalW, height: naturalH },
        },
      }).unwrap();
      // Redux/RTK uppdaterar -> selectors -> useEffect -> setNodes(flowNodes)
    } catch {
      dispatch(setShowFailedMessageThunk("Kunne ikke opprette bilde-node"));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const isValidConnection = useCallback(
    (conn: Connection) => {
      const s = nodes.find((n) => n.id === conn.source);
      const t = nodes.find((n) => n.id === conn.target);
      return isIssueNode(s) && isIssueNode(t) && s.id !== t.id;
    },
    [nodes],
  );

  const openIssueModal = useCallback(
    (issueId: string) => {
      dispatch(
        addAppModalThunk({
          itemId: issueId,
          boardId: issueBoardId,
          projectId,
          app: AppModalEnums.ISSUE,
        }),
      );
    },
    [dispatch, projectId, issueBoardId],
  );

  const edgeColor = "#5b5b5b";

  return (
    <>
      <Flex className="mx-1" gap="0" w="100%" h="100%" style={{ alignItems: "stretch" }}>
        <Box
          style={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            position: "relative",
          }}
        >
          <ReactFlow
            ref={rfRef}
            nodes={nodes}
            edges={edges}
            nodeTypes={{ issueNode: KanbanIssueNode, imageNode: LappeplanImage }}
            fitView
            minZoom={0.05}
            nodesDraggable
            color="#fff"
            nodesConnectable
            selectNodesOnDrag={false}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={(conn) =>
              connectIssues({
                body: {
                  projectId,
                  issueBoardId,
                  sourceIssueId: conn.source,
                  targetIssueId: conn.target,
                  action: "CONNECT",
                },
              })
            }
            isValidConnection={isValidConnection}
            deleteKeyCode={["Delete", "Backspace"]}
            snapToGrid
            snapGrid={[20, 20]}
            onNodeDoubleClick={(evt, node) => {
              if (node.type === "issueNode") {
                openIssueModal(node.id);
              }
            }}
            defaultEdgeOptions={{
              // use curves for all edges that don't specify a type
              style: { stroke: edgeColor, strokeWidth: 2 },
            }}
            connectionLineType={ConnectionLineType.Bezier}
            connectionLineStyle={{ stroke: edgeColor, strokeWidth: 2 }}
            // onMoveEnd={onMoveEnd}
          >
            <Background id="grid" gap={80} lineWidth={1} color="#fff" variant={BackgroundVariant.Lines} />

            {/* Panel för att lägga till bilder */}
            <div style={{ position: "absolute", top: 16, left: 16, zIndex: 10 }}>
              <Button size="sm" onClick={() => fileInputRef.current?.click()}>
                Legg til bilde
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handlePickImage}
              />
            </div>
          </ReactFlow>
        </Box>

        <SidePanel getCenter={getCenter} />
      </Flex>
    </>
  );
};

const SidePanel: React.FC<{ getCenter: () => XYPosition }> = ({ getCenter }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchText, setSearchText] = useState("");

  const dispatch = useAppDispatch();
  const [updateIssue, { isLoading: updateLoading }] = useDoUpdateIssueMutation();

  const issuesWithoutNodes = useAppSelector(selectIssuesWithoutNoteboardNode);

  const projectId = useAppSelector(selectCurrentProjectId);
  const issueBoardId = useAppSelector(selectIssueBoardId);

  const getOpenIssueArgs = useCallback(
    (id: string): IOpenedAppModal => ({
      itemId: id,
      boardId: issueBoardId,
      projectId: projectId,
      app: AppModalEnums.ISSUE,
    }),
    [issueBoardId, projectId],
  );

  const hiddenOverflow: CellStyle = { overflow: "hidden" };

  const columns = useMemo<ColDef<IIssueApi>[]>(
    () => [
      {
        field: "_id",
        headerName: "",
        width: 40,
        resizable: false,
        sortable: false,
        filter: false,
        cellClass: "d-flex align-items-center justify-content-center px-0",
        cellRenderer: (params: ICellRendererParams<IIssueApi, string>) => (
          <Button
            size="sm"
            px={"xs"}
            variant="subtle"
            disabled={updateLoading}
            onClick={async () => {
              const issueId = params.data?._id;
              if (!issueId) return;
              const pos = getCenter(); // färsk center-pos
              try {
                await updateIssue({
                  projectId,
                  issueBoardId,
                  issueId,
                  attr: "noteboardNode",
                  value: { visible: true, x: pos.x, y: pos.y },
                  oldValue: null,
                }).unwrap();
                // här kan du optimistiskt patcha noteboard-cache om du vill
              } catch (e) {
                dispatch(setShowFailedMessageThunk("Kunne ikke oppdatere sak"));
                console.error("Kunne ikke opprette node", e);
              }
            }}
            title="Legg til node"
          >
            <i className="fa fa-long-arrow-left" />
          </Button>
        ),
      },
      {
        field: "id",
        headerName: "ID",
        width: 70,
      },
      {
        field: "title",
        headerName: "Tittel",
        flex: 1,
        minWidth: 140,
        cellClass: "d-flex align-items-center pl-3 pr-0",
        cellRenderer: (params: ICellRendererParams<IIssueApi, string>) => (
          <div
            className="w-100 text-truncate"
            style={{ cursor: "pointer" }}
            onClick={() => {
              const id = params.data?._id;
              if (!id) return;
              dispatch(addAppModalThunk(getOpenIssueArgs(id)));
            }}
            title={params.value ?? ""}
          >
            {params.value}
          </div>
        ),
        cellStyle: hiddenOverflow,
      },
    ],
    [updateLoading, updateIssue, projectId, issueBoardId, getCenter, getOpenIssueArgs, hiddenOverflow, dispatch],
  );

  const getRowId = useCallback((p: GetRowIdParams<IIssueApi>) => p.data!._id, []);

  return (
    <div className="d-flex border position-relative h-100">
      <div
        className="mc-gray-200"
        style={{
          width: 30,
          cursor: "pointer",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={() => setIsCollapsed((v) => !v)}
        aria-label="Toggle sidepanel"
        title={isCollapsed ? "Åpne sidepanel" : "Skjul sidepanel"}
      >
        <i className={isCollapsed ? "fa fa-angle-double-left fa-fw fa-2x" : "fa fa-angle-double-right fa-fw fa-2x"} />
      </div>

      <div
        style={{
          width: isCollapsed ? 0 : 500,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          transition: "width 0.3s cubic-bezier(.4,0,.2,1)",
          minWidth: 0,
        }}
      >
        {!isCollapsed && (
          <>
            <div className="p-2 border-bottom bg-white" style={{ flex: "0 0 auto" }}>
              <TextInput
                size="sm"
                placeholder="Søk…"
                value={searchText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
              />
            </div>

            <div style={{ flex: "1 1 auto", minHeight: 0 }}>
              <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }}>
                <AgGridReact<IIssueApi>
                  rowData={issuesWithoutNodes}
                  columnDefs={columns}
                  defaultColDef={{ suppressHeaderMenuButton: true }}
                  headerHeight={30}
                  rowHeight={35}
                  animateRows={false}
                  suppressClickEdit
                  suppressContextMenu
                  quickFilterText={searchText}
                  getRowId={getRowId}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


